import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input } from 'antd';
import useFlatsQuery from '../../../api/useFlatsQuery';
import { useRequestFilterCommands } from '../../../model/commandsContext';
import { createOptions, getLeafNodesValues } from './lib/options';
import { getCollectLeavesTypeFlat, useFilteredItems } from './lib/utils';
import SelectPorch from './ui/SelectPorch';
import SelectFloor from './ui/SelectFloor';
import SelectRoom from './ui/SelectRoom';
import SelectTypeFlat from './ui/SelectTypeFlat';

const selectRoomValue = (state) => state.filterStore.valueAddrFlat;
const selectPorchValue = (state) => state.filterStore.valueAddrPorch;
const selectFloorValue = (state) => state.filterStore.valueAddrFloor;
const selectTypeFlatValue = (state) => state.filterStore.valueAddrTypeFlat;
const selectHouse = (state) => state.filterStore.valueAddrBuilding;

const getPopupContainer = (triggerNode) => triggerNode.parentElement;

const SelectRoomsRow = () => {
  const filterCommands = useRequestFilterCommands();
  const roomValue = useSelector(selectRoomValue);
  const porchValue = useSelector(selectPorchValue);
  const floorValue = useSelector(selectFloorValue);
  const typeFlatValue = useSelector(selectTypeFlatValue);
  const houseValue = useSelector(selectHouse);

  const { data: roomList = [] } = useFlatsQuery(houseValue?.[0], {
    enabled: !!houseValue?.length,
  });

  const dispatch = useDispatch();

  const { porchs, floors, gridRooms, rooms } = useMemo(
    () => createOptions(roomList),
    [roomList]
  );

  const showRooms = !porchValue?.length && !floorValue?.length;

  const { items: filteredFloors } = useFilteredItems({
    items: floors,
    enabled: Boolean(porchValue?.length),
    value: porchValue,
  });

  const getRoomValByPorchFloor = useCallback(
    (nodes) => getLeafNodesValues({ nodes }),
    []
  );

  const { items: filteredGridRooms, cbData: roomValueByPorchFloor } =
    useFilteredItems({
      items: gridRooms,
      enabled: Boolean(porchValue?.length || floorValue?.length),
      value: floorValue?.length ? floorValue : porchValue,
      cb: getRoomValByPorchFloor,
    });

  const typeFlatItems = useMemo(
    () =>
      getCollectLeavesTypeFlat([
        ...filteredGridRooms,
        ...(showRooms ? rooms : []),
      ]),
    [filteredGridRooms, rooms, showRooms]
  );

  useEffect(() => {
    dispatch(filterCommands.setFlatValueByPorchFloor(roomValueByPorchFloor));
  }, [dispatch, filterCommands, roomValueByPorchFloor]);

  return (
    <Input.Group compact>
      <SelectTypeFlat
        value={typeFlatValue}
        getPopupContainer={getPopupContainer}
        items={Object.values(typeFlatItems)}
      />
      <SelectRoom
        value={roomValue}
        getPopupContainer={getPopupContainer}
        gridRooms={filteredGridRooms}
        rooms={rooms}
        showRooms={showRooms}
      />
      <SelectPorch
        value={porchValue}
        getPopupContainer={getPopupContainer}
        items={porchs}
      />
      <SelectFloor
        value={floorValue}
        getPopupContainer={getPopupContainer}
        items={filteredFloors}
      />
    </Input.Group>
  );
};

export default memo(SelectRoomsRow);
